import { type Team, type UserTeam } from '@rentguru/commons-utils';
import { graphqlClient } from '@up2rent/graphql';

export interface TeamApiRow {
  id: string;
  name: string;
  parentId: string | null;
}

export interface UserTeamApiRow {
  id: string;
  userId: string;
  dynamoUserId: string | null;
  teamId: string;
}

export interface UserRightApiEntry {
  section: string;
  access: string[];
}

export interface UserRoleApiRow {
  id: string;
  name: string;
  userRights: UserRightApiEntry[];
  isDefault: boolean;
}

interface GqlTeam {
  id: string;
  clientId: string;
  name: string;
  parentId: string | null;
}

interface GqlUserTeam {
  id: string;
  clientId: string;
  userId: string;
  dynamoUserId: string | null;
  teamId: string;
}

interface GqlUserRole {
  id: string;
  clientId: string;
  name: string;
  isDefault: boolean;
  userRights: UserRightApiEntry[];
}

const TEAM_FIELDS = 'id clientId name parentId';
const USER_TEAM_FIELDS = 'id clientId userId dynamoUserId teamId';
const USER_ROLE_FIELDS = 'id clientId name isDefault userRights { section access }';

const TEAMS_AND_MEMBERSHIPS_QUERY = `query TeamManagementTeams {
  teamManagement { teams { ${TEAM_FIELDS} } userTeams { ${USER_TEAM_FIELDS} } }
}`;

const USER_ROLES_QUERY = `query TeamManagementRoles {
  teamManagement { userRoles { ${USER_ROLE_FIELDS} } }
}`;

const CREATE_TEAM_MUTATION = `mutation CreateAuthTeam($input: CreateAuthTeamInput!) {
  createAuthTeam(input: $input) { ${TEAM_FIELDS} }
}`;

const UPDATE_TEAM_MUTATION = `mutation UpdateAuthTeam($input: UpdateAuthTeamInput!) {
  updateAuthTeam(input: $input) { id }
}`;

const DELETE_TEAM_MUTATION = `mutation DeleteAuthTeam($input: DeleteAuthTeamInput!) {
  deleteAuthTeam(input: $input) { id }
}`;

const ADD_USER_TEAM_MUTATION = `mutation AddAuthUserTeam($input: AddAuthUserTeamInput!) {
  addAuthUserTeam(input: $input) { ${USER_TEAM_FIELDS} }
}`;

const REMOVE_USER_TEAM_MUTATION = `mutation RemoveAuthUserTeam($input: RemoveAuthUserTeamInput!) {
  removeAuthUserTeam(input: $input) { id }
}`;

const CREATE_USER_ROLE_MUTATION = `mutation CreateAuthUserRole($input: CreateAuthUserRoleInput!) {
  createAuthUserRole(input: $input) { ${USER_ROLE_FIELDS} }
}`;

const UPDATE_USER_ROLE_MUTATION = `mutation UpdateAuthUserRole($input: UpdateAuthUserRoleInput!) {
  updateAuthUserRole(input: $input) { id }
}`;

const DELETE_USER_ROLE_MUTATION = `mutation DeleteAuthUserRole($input: DeleteAuthUserRoleInput!) {
  deleteAuthUserRole(input: $input) { id }
}`;

const SET_MEMBER_ROLE_MUTATION = `mutation SetAuthMemberRole($input: SetAuthMemberRoleInput!) {
  setAuthMemberRole(input: $input) { userId userRoleId }
}`;

const SET_MEMBER_STATUS_MUTATION = `mutation SetAuthMemberStatus($input: SetAuthMemberStatusInput!) {
  setAuthMemberStatus(input: $input) { userId status }
}`;

const toTeamRow = (team: GqlTeam): TeamApiRow => ({
  id: team.id,
  name: team.name,
  parentId: team.parentId,
});

const toUserTeamRow = (userTeam: GqlUserTeam): UserTeamApiRow => ({
  id: userTeam.id,
  userId: userTeam.userId,
  dynamoUserId: userTeam.dynamoUserId,
  teamId: userTeam.teamId,
});

const toUserRoleRow = (role: GqlUserRole): UserRoleApiRow => ({
  id: role.id,
  name: role.name,
  userRights: role.userRights,
  isDefault: role.isDefault,
});

export const toTeam = (row: TeamApiRow, clientId: string): Team => ({
  id: row.id,
  name: row.name,
  parentId: row.parentId,
  clientId,
  readId: '',
  _version: 1,
});

export const toUserTeam = (row: UserTeamApiRow, clientId: string, team?: Team | null): UserTeam => ({
  id: row.id,
  userId: row.userId,
  teamId: row.teamId,
  team,
  clientId,
  readId: '',
  _version: 1,
});

export const fetchTeamsAndMemberships = async (): Promise<{ teams: TeamApiRow[]; userTeams: UserTeamApiRow[] }> => {
  const data = await graphqlClient.request<{ teamManagement: { teams: GqlTeam[]; userTeams: GqlUserTeam[] } }>(
    TEAMS_AND_MEMBERSHIPS_QUERY
  );
  return {
    teams: data.teamManagement.teams.map(toTeamRow),
    userTeams: data.teamManagement.userTeams.map(toUserTeamRow),
  };
};

export const createTeamApi = async (input: {
  name: string;
  parentId?: string | null;
}): Promise<{ team: TeamApiRow }> => {
  const data = await graphqlClient.request<{ createAuthTeam: GqlTeam }>(CREATE_TEAM_MUTATION, { input });
  return { team: toTeamRow(data.createAuthTeam) };
};

export const updateTeamApi = async (input: {
  id: string;
  name?: string;
  parentId?: string | null;
}): Promise<{ status: boolean }> => {
  await graphqlClient.request(UPDATE_TEAM_MUTATION, { input });
  return { status: true };
};

export const deleteTeamApi = async (id: string): Promise<{ status: boolean }> => {
  await graphqlClient.request(DELETE_TEAM_MUTATION, { input: { id } });
  return { status: true };
};

export const addUserTeamApi = async (input: {
  userId: string;
  teamId: string;
}): Promise<{ userTeam: UserTeamApiRow }> => {
  const data = await graphqlClient.request<{ addAuthUserTeam: GqlUserTeam }>(ADD_USER_TEAM_MUTATION, { input });
  return { userTeam: toUserTeamRow(data.addAuthUserTeam) };
};

export const removeUserTeamApi = async (id: string): Promise<{ status: boolean }> => {
  await graphqlClient.request(REMOVE_USER_TEAM_MUTATION, { input: { id } });
  return { status: true };
};

export const fetchUserRolesApi = async (): Promise<{ roles: UserRoleApiRow[] }> => {
  const data = await graphqlClient.request<{ teamManagement: { userRoles: GqlUserRole[] } }>(USER_ROLES_QUERY);
  return { roles: data.teamManagement.userRoles.map(toUserRoleRow) };
};

export const createUserRoleApi = async (input: {
  name: string;
  userRights?: UserRightApiEntry[];
}): Promise<{ role: UserRoleApiRow }> => {
  const data = await graphqlClient.request<{ createAuthUserRole: GqlUserRole }>(CREATE_USER_ROLE_MUTATION, { input });
  return { role: toUserRoleRow(data.createAuthUserRole) };
};

export const updateUserRoleApi = async (input: {
  id: string;
  name?: string;
  userRights?: UserRightApiEntry[];
}): Promise<{ status: boolean }> => {
  await graphqlClient.request(UPDATE_USER_ROLE_MUTATION, { input });
  return { status: true };
};

export const deleteUserRoleApi = async (id: string): Promise<{ status: boolean }> => {
  await graphqlClient.request(DELETE_USER_ROLE_MUTATION, { input: { id } });
  return { status: true };
};

export const setMemberRoleApi = async (input: { userId: string; userRoleId: string }): Promise<{ status: boolean }> => {
  await graphqlClient.request(SET_MEMBER_ROLE_MUTATION, { input });
  return { status: true };
};

export const setMemberStatusApi = async (input: { userId: string; status: string }): Promise<{ status: boolean }> => {
  await graphqlClient.request(SET_MEMBER_STATUS_MUTATION, { input });
  return { status: true };
};
